import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/contexts/AuthContext';
import { Loader2, MessageCircle, Search, Crown, Check, X } from 'lucide-react';
import BackNavButton from '@/components/BackNavButton';
import DemoProfileBadge from '@/components/DemoProfileBadge';
import VerifiedBadge from '@/components/VerifiedBadge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

function getPhotoUrl(p) {
  if (!p) return null;
  if (p.startsWith('http')) return p;
  return `${window.location.origin}/api/files/${p}`;
}

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso); const now = new Date();
  const sec = Math.floor((now - d) / 1000);
  if (sec < 60) return 'now';
  if (sec < 3600) return `${Math.floor(sec/60)}m`;
  if (sec < 86400) return `${Math.floor(sec/3600)}h`;
  if (sec < 604800) return `${Math.floor(sec/86400)}d`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function TierBadge({ tier }) {
  if (tier === 'vip') {
    return <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-[#B38138] to-[#8B5E1A] text-white"><Crown size={9}/> VIP</span>;
  }
  if (tier === 'premium') {
    return <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full gold-gradient text-white"><Crown size={9}/> Premium</span>;
  }
  return null;
}

export default function Messages() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [intros, setIntros] = useState([]);
  const [introsSent, setIntrosSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('chats'); // 'chats' | 'intros'
  const [introTab, setIntroTab] = useState('received'); // 'received' | 'sent'
  const [search, setSearch] = useState('');
  const [actingId, setActingId] = useState(null);

  const loadAll = async () => {
    try {
      const [m, i, s] = await Promise.all([
        api.get('/matches'),
        api.get('/intros/received'),
        api.get('/intros/sent'),
      ]);
      setMatches(m.data.matches || []);
      setIntros(i.data.intros || []);
      setIntrosSent(s.data.intros || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const accept = async (intro) => {
    setActingId(intro.intro_id);
    try {
      const r = await api.post(`/intros/${intro.intro_id}/accept`);
      toast.success('Accepted! Opening chat');
      navigate(`/chat/${r.data.match_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept');
      setActingId(null);
    }
  };

  const decline = async (intro) => {
    setActingId(intro.intro_id);
    try {
      await api.post(`/intros/${intro.intro_id}/decline`);
      setIntros(prev => prev.filter(i => i.intro_id !== intro.intro_id));
      toast.success('Dismissed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setActingId(null);
    }
  };

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" /></div>;

  const convos = matches.filter(m => m.last_message);
  const filtered = search
    ? convos.filter(m => m.other_user?.display_name?.toLowerCase().includes(search.toLowerCase()) || (m.last_message?.text||'').toLowerCase().includes(search.toLowerCase()))
    : convos;
  const unreadCount = convos.filter(m => m.last_message?.sender_id !== user?.user_id && !(m.last_message?.read_by||[]).includes(user?.user_id)).length;
  const introCount = intros.length + introsSent.length;

  return (
    <div className="min-h-screen bg-white pb-[96px]" data-testid="messages-page">
      <div className="px-5 pt-8 pb-5 flex items-center gap-3">
        <BackNavButton />
        <h1 className="font-brand text-[32px] font-[600] tracking-tight leading-none">
          Messages {unreadCount > 0 && <span className="ml-2 text-xs align-middle brand-gradient text-white px-2 py-0.5 rounded-full">{unreadCount} new</span>}
        </h1>
      </div>

      {/* Tabs */}
      <div className="px-5 mb-3 flex gap-2">
        <button
          onClick={() => setTab('chats')}
          data-testid="tab-chats"
          className={`px-4 py-1.5 rounded-full text-[13px] font-semibold transition ${
            tab === 'chats' ? 'brand-gradient text-white' : 'bg-[var(--surface-2)] text-[#4B4B52] hover:bg-black/[0.05]'
          }`}
        >
          Chats
        </button>
        <button
          onClick={() => setTab('intros')}
          data-testid="tab-intros"
          className={`px-4 py-1.5 rounded-full text-[13px] font-semibold transition inline-flex items-center gap-1.5 ${
            tab === 'intros' ? 'brand-gradient text-white' : 'bg-[var(--surface-2)] text-[#4B4B52] hover:bg-black/[0.05]'
          }`}
        >
          Intros
          {introCount > 0 && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
              tab === 'intros' ? 'bg-white/25 text-white' : 'bg-[#B91C1C] text-white'
            }`}>{introCount}</span>
          )}
        </button>
      </div>

      {tab === 'chats' && (
        <div className="px-5 mb-3">
          <div className="relative">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A8A93]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} data-testid="message-search"
              placeholder="Search conversations"
              className="pl-10 bg-[var(--surface-2)] border-transparent rounded-full text-sm focus-visible:ring-[#B91C1C]/30 focus-visible:border-transparent focus-visible:bg-white" />
          </div>
        </div>
      )}

      {tab === 'chats' ? (
        filtered.length === 0 ? (
          <div className="flex items-center justify-center h-[50vh] px-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--surface-2)] flex items-center justify-center mx-auto mb-4"><MessageCircle className="w-7 h-7 text-[#8A8A93]" strokeWidth={1.5}/></div>
              <h2 className="font-display text-lg font-[600] mb-1">{search ? 'No results' : 'No messages yet'}</h2>
              <p className="text-[#8A8A93] text-sm">{search ? 'Try a different name' : 'Start a conversation from Matches or Discover.'}</p>
              {!search && (
                <button onClick={() => navigate('/matches')} className="mt-5 text-sm font-semibold text-[#B91C1C] hover:underline" data-testid="go-to-matches">See matches</button>
              )}
            </div>
          </div>
        ) : (
          <div className="px-2" data-testid="messages-list">
            {filtered.map(m => {
              const isMe = m.last_message?.sender_id === user?.user_id;
              const unread = m.last_message && !isMe && !(m.last_message?.read_by||[]).includes(user?.user_id);
              return (
                <button key={m.match_id} onClick={() => navigate(`/chat/${m.match_id}`)} data-testid={`message-row-${m.match_id}`}
                  className="w-full flex items-center gap-3 py-3 px-3 rounded-2xl hover:bg-black/[0.02] transition-colors text-left">
                  <div className="relative w-14 h-14 rounded-full overflow-hidden flex-shrink-0 bg-[var(--surface-2)]">
                    <img src={getPhotoUrl(m.other_user.photo_url) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${m.other_user.user_id}`}
                      alt="" className="w-full h-full object-cover"
                      onError={e => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${m.other_user.user_id}`; }} />
                    {unread && <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full brand-gradient border-2 border-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <p className={`${unread ? 'font-bold' : 'font-semibold'} text-[15px] truncate text-[#0F0F10]`}>{m.other_user.display_name}</p>
                        <VerifiedBadge badges={m.other_user.badges} size={14} data-testid="messages-verified-checkmark" />
                        {m.other_user.is_seed && <DemoProfileBadge isSeed={true} size="xs" />}
                      </div>
                      <span className={`text-[11px] flex-shrink-0 ml-2 ${unread ? 'font-bold text-[#B91C1C]' : 'text-[#8A8A93]'}`}>{relativeTime(m.last_message?.created_at)}</span>
                    </div>
                    <p className={`text-[13px] truncate ${unread ? 'text-[#0F0F10]' : 'text-[#8A8A93]'}`}>
                      {isMe && 'You: '}{m.last_message?.text}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )
      ) : (
        /* Intros tab */
        <>
          {/* Received / Sent sub-tabs */}
          <div className="px-5 mb-3 flex gap-2">
            <button
              onClick={() => setIntroTab('received')}
              data-testid="intro-subtab-received"
              className={`px-3 py-1 rounded-full text-[12px] font-semibold transition inline-flex items-center gap-1.5 ${
                introTab === 'received'
                  ? 'bg-[#B91C1C]/[0.08] text-[#B91C1C] ring-1 ring-[#B91C1C]/20'
                  : 'text-[#8A8A93] hover:text-[#0F0F10]'
              }`}
            >
              Received
              {intros.length > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  introTab === 'received' ? 'bg-[#B91C1C] text-white' : 'bg-black/10 text-[#4B4B52]'
                }`}>{intros.length}</span>
              )}
            </button>
            <button
              onClick={() => setIntroTab('sent')}
              data-testid="intro-subtab-sent"
              className={`px-3 py-1 rounded-full text-[12px] font-semibold transition inline-flex items-center gap-1.5 ${
                introTab === 'sent'
                  ? 'bg-[#B91C1C]/[0.08] text-[#B91C1C] ring-1 ring-[#B91C1C]/20'
                  : 'text-[#8A8A93] hover:text-[#0F0F10]'
              }`}
            >
              Sent
              {introsSent.length > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  introTab === 'sent' ? 'bg-[#B91C1C] text-white' : 'bg-black/10 text-[#4B4B52]'
                }`}>{introsSent.length}</span>
              )}
            </button>
          </div>

          {introTab === 'received' ? (
            intros.length === 0 ? (
              <div className="flex items-center justify-center h-[40vh] px-6">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[var(--surface-2)] flex items-center justify-center mx-auto mb-4"><MessageCircle className="w-7 h-7 text-[#8A8A93]" strokeWidth={1.5}/></div>
                  <h2 className="font-display text-lg font-[600] mb-1">No intros yet</h2>
                  <p className="text-[#8A8A93] text-sm">When someone sends you a direct message, they'll appear here. VIP and Premium messages show first.</p>
                </div>
              </div>
            ) : (
              <div className="px-4 space-y-3" data-testid="intros-list">
                {intros.map(intro => {
                  const sender = intro.sender || {};
                  const acting = actingId === intro.intro_id;
                  return (
                    <div key={intro.intro_id} data-testid={`intro-${intro.intro_id}`}
                      className="flex gap-3 p-3 rounded-2xl border border-[var(--hairline)] bg-white hover:shadow-sm transition">
                      <img src={getPhotoUrl(sender.photo_url) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${sender.user_id}`}
                        alt="" className="w-14 h-14 rounded-full object-cover bg-[var(--surface-2)] flex-shrink-0"
                        onError={e => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${sender.user_id}`; }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <p className="font-semibold text-[14px] truncate text-[#0F0F10]">
                            {sender.display_name || 'Someone'}{sender.age ? `, ${sender.age}` : ''}
                          </p>
                          <TierBadge tier={intro.sender_tier} />
                          <span className="text-[11px] text-[#8A8A93] ml-auto">{relativeTime(intro.created_at)}</span>
                        </div>
                        <p className="text-[13px] text-[#4B4B52] leading-snug mb-2.5 italic">"{intro.message}"</p>
                        <div className="flex gap-2">
                          <button onClick={() => decline(intro)} disabled={acting} data-testid={`intro-decline-${intro.intro_id}`}
                            className="flex-1 py-2 rounded-full text-[12px] font-semibold text-[#4B4B52] border border-[var(--hairline-strong)] hover:bg-black/[0.03] inline-flex items-center justify-center gap-1 disabled:opacity-50 transition">
                            <X size={12}/> Dismiss
                          </button>
                          <button onClick={() => accept(intro)} disabled={acting} data-testid={`intro-accept-${intro.intro_id}`}
                            className="flex-1 py-2 rounded-full text-[12px] font-semibold text-white brand-gradient brand-glow-sm hover:scale-[1.02] inline-flex items-center justify-center gap-1 disabled:opacity-50 transition">
                            {acting ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>} Accept & chat
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            /* Sent sub-tab */
            introsSent.length === 0 ? (
              <div className="flex items-center justify-center h-[40vh] px-6">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[var(--surface-2)] flex items-center justify-center mx-auto mb-4"><MessageCircle className="w-7 h-7 text-[#8A8A93]" strokeWidth={1.5}/></div>
                  <h2 className="font-display text-lg font-[600] mb-1">No sent intros</h2>
                  <p className="text-[#8A8A93] text-sm">Send a direct message from any profile in Discover. Buy Warm Intros on Free, 3/day on Premium, 5/day on VIP.</p>
                  <button onClick={() => navigate('/discover')} className="mt-5 text-sm font-semibold text-[#B91C1C] hover:underline">Find someone to message</button>
                </div>
              </div>
            ) : (
              <div className="px-4 space-y-3" data-testid="intros-sent-list">
                {introsSent.map(intro => {
                  const r = intro.recipient || {};
                  const status = intro.status;
                  const statusColor = status === 'accepted'
                    ? 'text-[#15803D] bg-[#15803D]/10'
                    : status === 'declined'
                      ? 'text-[#8A8A93] bg-black/[0.05]'
                      : 'text-[#B38138] bg-[#FDF7EC]';
                  const statusLabel = status === 'accepted' ? 'Accepted'
                    : status === 'declined' ? 'Dismissed'
                    : 'Pending';
                  return (
                    <div key={intro.intro_id} data-testid={`intro-sent-${intro.intro_id}`}
                      className="flex gap-3 p-3 rounded-2xl border border-[var(--hairline)] bg-white">
                      <img src={getPhotoUrl(r.photo_url) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${r.user_id}`}
                        alt="" className="w-14 h-14 rounded-full object-cover bg-[var(--surface-2)] flex-shrink-0"
                        onError={e => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${r.user_id}`; }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <p className="font-semibold text-[14px] truncate text-[#0F0F10]">
                            To {r.display_name || 'Someone'}
                          </p>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusColor}`}>{statusLabel}</span>
                          <span className="text-[11px] text-[#8A8A93] ml-auto">{relativeTime(intro.created_at)}</span>
                        </div>
                        <p className="text-[13px] text-[#4B4B52] leading-snug italic mb-1">"{intro.message}"</p>
                        {status === 'accepted' && intro.match_id && (
                          <button onClick={() => navigate(`/chat/${intro.match_id}`)}
                            data-testid={`intro-sent-open-chat-${intro.intro_id}`}
                            className="text-[12px] font-semibold text-[#B91C1C] hover:underline">
                            Open chat →
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
